import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { searchVideos, sortVideos, formatPlayCount } from "@/utils/video";
import VideoCard from "@/components/base/VideoCard";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    setKeyword(query);
    setVisibleCount(12);
    document.title = query ? `“${query}” 搜索结果 - 色色视频` : "搜索视频 - 色色视频";
  }, [query]);

  const results = useMemo(() => (query ? sortVideos(searchVideos(query), "latest") : []), [query]);
  const hotRecommend = useMemo(() => sortVideos(videos, "play").slice(0, 8), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    }
  };

  const visibleResults = results.slice(0, visibleCount);
  const hasMore = visibleCount < results.length;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6">
      <form
        onSubmit={submit}
        className="mx-auto flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5"
        role="search"
      >
        <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true"></i>
        <input
          type="search"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder="输入关键词，回车开始搜索"
          aria-label="搜索视频"
          className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none md:text-base"
        />
        <button
          type="submit"
          className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {query && (
        <h1 className="mt-8 font-heading text-2xl text-foreground-950 md:text-3xl">
          搜索结果
        </h1>
      )}

      {query && (
        <p className="mt-2 text-sm text-foreground-500">
          关键词 <strong className="text-primary-600">“{query}”</strong> 共匹配到{" "}
          <strong className="text-primary-600">{results.length}</strong> 个视频
        </p>
      )}

      {!query && (
        <div className="mt-10 rounded-xl border border-background-200 bg-background-100 p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
          </span>
          <h2 className="mt-4 font-heading text-2xl text-foreground-950">发现你想看的内容</h2>
          <p className="mt-2 text-sm text-foreground-500">
            输入电影、电视剧、动漫、纪录片等关键词，快速找到感兴趣的视频。
          </p>
        </div>
      )}

      {query && results.length === 0 && (
        <div className="mt-16 flex flex-col items-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
            <i className="ri-search-line text-3xl" aria-hidden="true"></i>
          </span>
          <h2 className="mt-5 font-heading text-2xl text-foreground-950">没有找到相关视频</h2>
          <p className="mt-2 text-sm text-foreground-500">
            换个关键词试试，或者看看下面的热门推荐内容。
          </p>
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              setSearchParams({});
            }}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line text-lg" aria-hidden="true"></i>
            重置搜索
          </button>
        </div>
      )}

      {visibleResults.length > 0 && (
        <>
          <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
            {visibleResults.map((video, index) => (
              <VideoCard key={video.id} video={video} index={index} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + 12)}
                className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-add-line text-lg" aria-hidden="true"></i>
                加载更多
              </button>
            </div>
          )}
        </>
      )}

      {(!query || results.length === 0) && (
        <section className="mt-14">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-7 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">热门推荐</h2>
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
            {hotRecommend.map((video, index) => (
              <VideoCard key={video.id} video={video} index={index} />
            ))}
          </div>
        </section>
      )}

      {query && results.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2 rounded-xl border border-background-200 bg-background-100 p-4">
          <span className="text-sm text-foreground-500">换个分类看看：</span>
          {[
            { slug: "movie", name: "电影" },
            { slug: "tv", name: "电视剧" },
            { slug: "anime", name: "动漫" },
            { slug: "documentary", name: "纪录片" },
            { slug: "variety", name: "综艺" },
          ].map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="rounded-full bg-background-50 px-3.5 py-1.5 text-sm text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-600"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}